import { MoveDown, MoveUp } from 'lucide-react'
import * as React from 'react'
import { twMerge } from 'tailwind-merge'

import { SortOrder } from '@/hooks/useSortTable'

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement> & {
    backofficeScroll?: boolean
    sidebarClosed?: boolean
    isNotBackoffice?: boolean
  }
>(
  (
    { className, backofficeScroll, sidebarClosed, isNotBackoffice, ...props },
    ref,
  ) => (
    <div
      className={twMerge(
        'relative w-full overflow-auto',
        sidebarClosed && 'w-[calc(100vw-5rem-64px)]',
        backofficeScroll &&
          !sidebarClosed &&
          'w-[85vw] overflow-x-auto transition-all lg:w-[calc(100vw-16rem-64px)]',
        isNotBackoffice && 'w-full',
      )}
    >
      <table
        ref={ref}
        className={twMerge(
          'w-full table-fixed caption-bottom border-separate border-spacing-y-2 text-monochromatic-500',
          className,
          backofficeScroll && 'w-max min-w-full',
          isNotBackoffice && 'w-max min-w-full',
        )}
        {...props}
      />
    </div>
  ),
)
Table.displayName = 'Table'

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={twMerge(
      '[&_tr>th:first-child]:rounded-l-lg [&_tr>th:last-child]:rounded-r-lg [&_tr]:bg-bios-primary/30',
      className,
    )}
    {...props}
  />
))
TableHeader.displayName = 'TableHeader'

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={twMerge(
      '[&_tr:hover]:shadow-table-row',
      '[&_tr>td:first-child]:rounded-l-lg [&_tr>td:last-child]:rounded-r-lg',
      '[&_tr>td]:bg-white',
      className,
    )}
    {...props}
  />
))
TableBody.displayName = 'TableBody'

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot ref={ref} className={twMerge('font-medium', className)} {...props} />
))
TableFooter.displayName = 'TableFooter'

interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  withHover?: boolean
}

const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, withHover = true, ...props }, ref) => (
    <tr
      ref={ref}
      className={twMerge(withHover && 'group', className)}
      {...props}
    />
  ),
)
TableRow.displayName = 'TableRow'

interface TableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  sortOrder?: SortOrder
  isCenter?: boolean
}

const TableHead = React.forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, sortOrder, isCenter = false, ...props }, ref) => (
    <th
      ref={ref}
      className={twMerge(
        'h-14 px-1 text-left align-middle font-bold',
        sortOrder !== undefined && 'cursor-pointer',
        className,
      )}
      {...props}
    >
      {sortOrder !== undefined ? (
        <div
          className={twMerge(
            'flex items-center gap-1',
            isCenter && 'justify-center',
          )}
        >
          {props.children}
          {sortOrder && (sortOrder === 'asc' ? <MoveDown /> : <MoveUp />)}
        </div>
      ) : (
        props.children
      )}
    </th>
  ),
)
TableHead.displayName = 'TableHead'

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={twMerge(
      'h-12 border border-transparent px-1 align-middle transition-colors',
      'group-hover:cursor-pointer group-hover:border-y-bios-primary first:group-hover:border-l-bios-primary last:group-hover:border-r-bios-primary',
      className,
    )}
    {...props}
  />
))
TableCell.displayName = 'TableCell'

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={twMerge('mt-4 text-sm', className)}
    {...props}
  />
))
TableCaption.displayName = 'TableCaption'

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
}
